import { motion, useMotionValueEvent, useSpring } from 'framer-motion';
import { useEffect, useState } from 'react';

function formatCurrency(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  } catch {
    // Bad/unknown currency code — fall back to a plain number.
    return Math.round(cents / 100).toLocaleString();
  }
}

interface AnimatedAmountProps {
  valueCents: number;
  currency: string;
  className?: string;
}

/**
 * Currency amount that springs smoothly to its target whenever it changes. The
 * spring is seeded with the initial value so it doesn't animate up from zero on
 * first paint — only subsequent ticks roll.
 */
export function AnimatedAmount({
  valueCents,
  currency,
  className,
}: AnimatedAmountProps) {
  const spring = useSpring(valueCents, {
    stiffness: 55,
    damping: 18,
    mass: 1,
  });
  const [display, setDisplay] = useState(valueCents);

  useEffect(() => {
    spring.set(valueCents);
  }, [valueCents, spring]);

  useMotionValueEvent(spring, 'change', (v) => setDisplay(v));

  return (
    <motion.span className={className} aria-live="polite">
      {formatCurrency(display, currency)}
    </motion.span>
  );
}
